import { getApiErrorMessage } from "./apiErrorMessage";

const GENERIC_UPLOAD_FAIL =
  "Upload failed. Please check the Excel file and try again.";

/**
 * Walk common API / Axios / batch error shapes for a user-facing message.
 * Does not invent Excel empty-sheet copy.
 */
const GENERIC_AXIOS_STATUS = /^request failed with status code \d+$/i;

export function extractUploadErrorMessage(source, fallback = "") {
  if (source == null) return fallback;

  const seen = new Set();
  const visitBody = (value, depth) => {
    if (value == null || depth > 5) return "";
    if (typeof value === "string") {
      const text = value.trim();
      if (!text || /<!DOCTYPE html|<html/i.test(text)) return "";
      if (GENERIC_AXIOS_STATUS.test(text)) return "";
      return text;
    }
    if (typeof value !== "object") return "";
    if (seen.has(value)) return "";
    seen.add(value);

    for (const key of ["message", "errorMessage", "reason", "detail"]) {
      const found = visitBody(value[key], depth + 1);
      if (found) return found;
    }

    if (typeof value.error === "string") {
      const found = visitBody(value.error, depth + 1);
      if (found) return found;
    } else if (value.error && typeof value.error === "object") {
      const found = visitBody(value.error, depth + 1);
      if (found) return found;
    }

    if (Array.isArray(value.errors) && value.errors.length) {
      const first = visitBody(value.errors[0], depth + 1);
      if (first) return first;
    }

    if (value.data && value.data !== value) {
      const nested = visitBody(value.data, depth + 1);
      if (nested) return nested;
    }

    return "";
  };

  const axiosData = source?.response?.data;
  const fromAxios = visitBody(axiosData, 0);
  if (fromAxios) return fromAxios;

  const fromSource = visitBody(source, 0);
  if (fromSource) return fromSource;

  const mapped = getApiErrorMessage(source, "");
  if (mapped && !GENERIC_AXIOS_STATUS.test(mapped)) return mapped;

  return fallback;
}

/**
 * Local file-picker checks only. Header / empty-sheet validation is server-side
 * (first non-empty row is the header).
 */
export async function validateUploadExcelFile(file) {
  if (!file) {
    return { ok: false, message: "Please select an Excel file first." };
  }

  const name = String(file.name || "").toLowerCase();
  if (!/\.(xlsx|xls)$/i.test(name)) {
    return { ok: false, message: "Please upload an .xlsx or .xls Excel file." };
  }

  if (!Number(file.size)) {
    return { ok: false, message: "The selected file is empty." };
  }

  return {
    ok: true,
    rowCount: null,
    columnCount: null,
    deferredValidation: true,
  };
}

export function getUploadErrorMessage(err, fallback = GENERIC_UPLOAD_FAIL) {
  const status = err?.response?.status;
  const extracted = extractUploadErrorMessage(err, "");

  if (status === 401) return "You are not signed in. Please log in again.";
  if (status === 403) {
    return "You do not have permission to upload records for this company.";
  }
  if (status === 404) {
    return (
      extracted ||
      "This upload was replaced or removed. If you started another upload for the same bank, wait for that one to finish."
    );
  }
  if (status === 413) {
    return extracted || "The Excel file is too large to upload.";
  }
  if (status === 503) {
    return (
      extracted ||
      "Upload queue is not ready. Restart the API and worker, then retry."
    );
  }

  if (extracted) return extracted;
  return fallback || GENERIC_UPLOAD_FAIL;
}
